import { threadApi } from '@/lib/api/threads.api'

import type { CreateThread, ThreadCreationResult } from '@scrambl/shared'
import type {
  OfflineLedgerEntry,
  OfflineMutationEnvelope,
  OfflineRunnerContext,
} from '@scrambl/shared/offline'
import { CreateThreadSchema, isValidUUID } from '@scrambl/shared'
import {
  assertOfflineWriteOwner,
  createScopedOfflineMutationFn,
  OfflineWriteKindError,
} from '@scrambl/shared/offline'
import { SessionChangedError } from '@scrambl/shared/session-scope'

import type { ThreadDraftOwner } from './drafts'
import { threadDraftOwnerKey } from './drafts'

export interface OwnedThreadCreationPayload {
  commandVersion: 1
  commandOwner: ThreadDraftOwner
  data: CreateThread
}
export function ownedThreadCreationPayload(
  input: CreateThread,
  owner: ThreadDraftOwner,
): OwnedThreadCreationPayload {
  if (!threadDraftOwnerKey(owner) || owner.portalEntityId)
    throw new OfflineWriteKindError()
  if (
    input.expectedUserId !== undefined &&
    input.expectedUserId !== owner.userId
  )
    throw new OfflineWriteKindError()
  const data = CreateThreadSchema.parse({
    ...input,
    // The composer has no description field; the API defaults it the same
    // way. Without this every new discussion failed validation here, before
    // anything was sent.
    description: input.description ?? '',
    operationId: input.operationId ?? crypto.randomUUID(),
    expectedUserId: owner.userId,
  })
  return { commandVersion: 1, commandOwner: { ...owner }, data }
}
export function requireOwnedThreadCreation(
  envelope: OfflineMutationEnvelope,
): OwnedThreadCreationPayload {
  const payload = envelope.payload as OwnedThreadCreationPayload | null
  if (
    envelope.kind !== 'thread.create' ||
    !payload ||
    payload.commandVersion !== 1 ||
    !threadDraftOwnerKey(payload.commandOwner) ||
    payload.commandOwner.portalEntityId !== null ||
    payload.commandOwner.userId !== envelope.ownerId ||
    payload.commandOwner.organizationId !== envelope.organizationId ||
    !isValidUUID(envelope.idempotencyKey) ||
    payload.data?.operationId !== envelope.idempotencyKey ||
    payload.data?.expectedUserId !== envelope.ownerId ||
    !CreateThreadSchema.safeParse(payload.data).success
  )
    throw new OfflineWriteKindError()
  return payload
}
function confirmResult(
  row: ThreadCreationResult,
  envelope: OfflineMutationEnvelope<OwnedThreadCreationPayload>,
): ThreadCreationResult {
  if (
    !row ||
    !isValidUUID(row.id) ||
    row.organizationId !== envelope.organizationId ||
    row.userId !== envelope.ownerId ||
    (envelope.payload.data.initialMessage &&
      (!row.initialMessageId ||
        !isValidUUID(row.initialMessageId) ||
        row.initialMessageId === row.id))
  )
    throw new OfflineWriteKindError()
  return row
}
/** One shared FIFO/durable dispatch marker/retry ceiling/receipt for creation. */
export function createThreadCreationCommandFn(
  context: OfflineRunnerContext,
  selectedOwner: () => ThreadDraftOwner | null,
  signal?: AbortSignal,
) {
  return async (
    envelope: OfflineMutationEnvelope<OwnedThreadCreationPayload>,
  ): Promise<ThreadCreationResult> => {
    assertOfflineWriteOwner(envelope, context.getOwnerId())
    if (envelope.organizationId !== selectedOwner()?.organizationId)
      throw new SessionChangedError()
    const payload = requireOwnedThreadCreation(envelope)
    const selected = threadDraftOwnerKey(selectedOwner())
    const captured = context.captureSession?.()
    const assertCurrent = () => {
      captured?.assertCurrent()
      signal?.throwIfAborted()
      if (
        !selected ||
        selected !== threadDraftOwnerKey(payload.commandOwner) ||
        selected !== threadDraftOwnerKey(selectedOwner())
      )
        throw new SessionChangedError()
    }
    assertCurrent()
    const dispatch = createScopedOfflineMutationFn<
      OwnedThreadCreationPayload,
      ThreadCreationResult
    >(
      'thread.create',
      async original => {
        assertCurrent()
        const row = await threadApi.createThread(
          original.payload.data,
          undefined,
          { signal, portalEntityId: null },
        )
        assertCurrent()
        return confirmResult(row, original)
      },
      {
        ...context,
        captureSession: () => ({
          assertCurrent,
          confirmCurrent: captured?.confirmCurrent?.bind(captured),
        }),
      },
      {
        organizationId: () => selectedOwner()?.organizationId ?? null,
        assertCurrent,
      },
    )
    const row = await dispatch(envelope)
    assertCurrent()
    return confirmResult(row, envelope)
  }
}
export type ThreadCreationEntry = Omit<OfflineLedgerEntry, 'envelope'> & {
  envelope: OfflineMutationEnvelope<OwnedThreadCreationPayload>
}
export function selectThreadCreationEntries(
  entries: readonly OfflineLedgerEntry[],
  owner: ThreadDraftOwner | null,
  parent?: { moduleType?: string; moduleId?: string },
): ThreadCreationEntry[] {
  const selected = threadDraftOwnerKey(owner)
  if (!selected || owner?.portalEntityId) return []
  return entries.filter((entry): entry is ThreadCreationEntry => {
    try {
      const payload = requireOwnedThreadCreation(entry.envelope)
      return (
        threadDraftOwnerKey(payload.commandOwner) === selected &&
        (!parent ||
          ((payload.data.moduleType ?? null) === (parent.moduleType ?? null) &&
            (payload.data.moduleId ?? null) === (parent.moduleId ?? null)))
      )
    } catch {
      return false
    }
  })
}
